import React, { useState, useEffect, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Icon } from "@iconify-icon/react";
import { useStore } from "../../../stores/StoreContext";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import { DocumentFormData, FormField, OptionItem } from "./Documents.types";
import DateField from "../../../HelperComponents/Form/Components/DateField";
import DocumentDropdown from "./DocumentDropdown";

import {
  formatBytes,
  buildDocumentMetadata,
  replaceContextValues,
  addTimeStampToFileName,
} from "../../../Utility/Utility";
import PBSPeoplePicker from "../../../HelperComponents/Form/Components/PBSPeoplePicker";
import FieldWrapper from "../../../HelperComponents/Form/Components/FieldWrapper";

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50mb

/**
 * UploadDocument Component
 * Handles document upload with drag-and-drop, progress tracking, and dynamic form generation
 */
const UploadDocument: React.FC = observer(() => {
  const { documentStore, uiStore, userStore } = useStore();
  const user = userStore.user;

  // Local state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<
    number | null
  >(null);
  const [formData, setFormData] = useState<DocumentFormData>({});
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const lastFetchedRoleRegionRef = useRef<{
    role: string;
    region: string;
  } | null>(null);

  // Region toggle on the Documents page: the user's region, or their approved new region
  const selectedRegion = documentStore.selectedRegion;

  // Load document types on mount (only once per role/region combination)
  useEffect(() => {
    if (user?.Role && selectedRegion && !isLoadingTypes) {
      const currentRoleRegion = { role: user.Role, region: selectedRegion };
      const lastFetched = lastFetchedRoleRegionRef.current;

      // Only fetch if role/region changed or if we haven't fetched yet
      if (
        !lastFetched ||
        lastFetched.role !== currentRoleRegion.role ||
        lastFetched.region !== currentRoleRegion.region
      ) {
        setIsLoadingTypes(true);

        documentStore
          .fetchDocumentTypes(user.Role, selectedRegion)
          .finally(() => {
            setIsLoadingTypes(false);
            lastFetchedRoleRegionRef.current = currentRoleRegion;
          });
      }
    }
  }, [user?.Role, selectedRegion, documentStore, isLoadingTypes]);

  // Load form schema when document type selected
  useEffect(() => {
    if (selectedDocumentTypeId && selectedDocumentTypeId > 0) {
      setIsLoadingSchema(true);
      setValidationError(null); // Clear previous errors

      documentStore
        .fetchDocumentFormTemplate(selectedDocumentTypeId)
        .catch((error) => {
          // Display form template errors inside the dialog
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load form template.";
          setValidationError(errorMessage);
        })
        .finally(() => {
          setIsLoadingSchema(false);
        });
    } else {
      // Clear form schema when no type selected
      documentStore.clearFormSchema();
      setFormData({});
      setValidationError(null);
    }
  }, [selectedDocumentTypeId, documentStore]);

  // Handle form field changes
  const handleFormFieldChange = useCallback((fieldId: string, value: any) => {
    // 🔍 DEBUG: Log form field changes

    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  // Reset form when dialog closes
  useEffect(() => {
    if (!uiStore.isUploadDialogOpen) {
      setSelectedFile(null);
      setSelectedDocumentTypeId(null);
      setFormData({});
      setIsDragging(false);
      setValidationError(null);
      documentStore.resetUploadProgress();
    }
  }, [uiStore.isUploadDialogOpen, documentStore]);

  // Prevent background scrolling while the dialog is open
  useEffect(() => {
    if (!uiStore.isUploadDialogOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [uiStore.isUploadDialogOpen]);

  // Render dynamic form field based on field type
  const renderFormField = useCallback(
    (field: FormField) => {
      const value = formData[field.name] ?? field.defaultValue ?? "";
      const fieldId = `field-${field.name}`;

      // Debug logging to understand field types and options

      switch (field.type) {
        case "Text":
        case "Note":
          return (
            <div key={fieldId} style={{ marginBottom: "16px" }}>
              <label
                htmlFor={fieldId}
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                {field.title || field.name}
                {field.required && (
                  <span style={{ color: "red", marginLeft: 4 }}>*</span>
                )}
              </label>

              <input
                id={fieldId}
                className="form-input"
                type="text"
                value={String(value)}
                onChange={(e) =>
                  handleFormFieldChange(field.name, e.target.value || "")
                }
                disabled={documentStore.isUploading}
                placeholder={field.placeholder}
                maxLength={field.maxlength}
              />

              {field.description && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#605e5c",
                    marginTop: "4px",
                  }}
                >
                  {field.description}
                </div>
              )}
            </div>
          );

        case "Number":
          return (
            <div key={fieldId} style={{ marginBottom: "16px" }}>
              <label
                htmlFor={fieldId}
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                {field.title || field.name}
                {field.required && (
                  <span style={{ color: "red", marginLeft: 4 }}>*</span>
                )}
              </label>
              <input
                id={fieldId}
                className="form-input"
                type="number"
                value={String(value)}
                onChange={(e) =>
                  handleFormFieldChange(
                    field.name,
                    e.target.value ? parseFloat(e.target.value) : 0,
                  )
                }
                disabled={documentStore.isUploading}
                placeholder={field.placeholder}
              />
              {field.description && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#605e5c",
                    marginTop: "4px",
                  }}
                >
                  {field.description}
                </div>
              )}
            </div>
          );

        case "Date":
        case "DateTime":
          return (
            <div key={fieldId} style={{ marginBottom: "16px" }}>
              <label
                htmlFor={fieldId}
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                {field.title || field.name}
                {field.required && (
                  <span style={{ color: "red", marginLeft: 4 }}>*</span>
                )}
              </label>
              <DateField
                placeholder={field.placeholder}
                id={fieldId}
                value={value}
                onChange={(date) =>
                  handleFormFieldChange(field.name, date ?? null)
                }
              />
            </div>
          );

        case "Boolean":
        case "Checkbox":
          return (
            <div key={fieldId} style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "14px",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  onChange={(e) =>
                    handleFormFieldChange(field.name, e.target.checked)
                  }
                  disabled={documentStore.isUploading}
                  style={{ marginRight: 4 }}
                />
                {field.title || field.name}
              </label>
            </div>
          );

        case "Dropdown":
        case "Choice":
        case "List":
          let dropdownOptions: OptionItem[] = [];

          // Check if field has direct options array
          if (field.options && Array.isArray(field.options)) {
            // Handle both string arrays and OptionItem object arrays
            dropdownOptions = field.options.map(
              (opt: unknown, index: number) => {
                if (typeof opt === "string") {
                  return { key: opt, text: opt };
                } else if (opt && typeof opt === "object" && "text" in opt) {
                  const optItem = opt as {
                    text: string;
                    key?: string;
                    value?: string;
                  };
                  return {
                    key:
                      optItem.key ||
                      optItem.value ||
                      optItem.text ||
                      String(index),
                    text: optItem.text,
                  };
                } else {
                  return { key: String(opt), text: String(opt) };
                }
              },
            );
          }
          // Check if field has optionsList reference and try to load from setupLists
          else if (
            field.optionsList &&
            documentStore.setupLists[field.optionsList]
          ) {
            dropdownOptions = documentStore.setupLists[field.optionsList]; // Already OptionItem[]
          }

          const fieldLabel = field.title || field.name;

          // If no options available, try to load them
          if (dropdownOptions.length === 0 && field.optionsList) {
            // Trigger loading of setup list options
            documentStore
              .loadSetupList(field.optionsList)
              .catch((error: unknown) => {});
          }

          return (
            <div key={fieldId} style={{ marginBottom: "16px" }}>
              <label
                htmlFor={fieldId}
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                {fieldLabel}
                {field.required && (
                  <span style={{ color: "red", marginLeft: 4 }}>*</span>
                )}
              </label>
              <DocumentDropdown
                id={fieldId}
                placeholder={
                  dropdownOptions.length === 0
                    ? "Loading options..."
                    : `Select ${fieldLabel}`
                }
                multi={field.multi}
                options={dropdownOptions}
                value={value}
                disabled={
                  documentStore.isUploading || dropdownOptions.length === 0
                }
                onChange={(next) => handleFormFieldChange(field.name, next)}
              />
            </div>
          );

        case "PeoplePicker":
          return (
            <FieldWrapper
              key={field.name}
              fieldId={field.name}
              label={field.title}
              description={field.description}
              required={field.required}
              error={field.error}
            >
              <PBSPeoplePicker
                id={field.name}
                label={field.title}
                multi={field.multi}
                required={field.required}
                value={value}
                hideLabel
                onChange={(val) => {
                  handleFormFieldChange(field.name, val);
                  // Field mapping is handled by onPersonaSelected callback
                }}
                onPersonaSelected={(persona: any) => {
                  // New callback to receive full persona data
                  const mappings = (field as any).fieldMappings;
                  if (persona && mappings) {
                    if (mappings.nameField && persona.name) {
                      handleFormFieldChange(mappings.nameField, persona.name);
                    }
                    if (mappings.emailField && persona.email) {
                      handleFormFieldChange(mappings.emailField, persona.email);
                    }
                  }
                }}
                placeholder={field.placeholder}
              />
            </FieldWrapper>
          );

        default:
          return null; // Unsupported field type - render nothing

        // return (
        //   <div key={fieldId} style={{ marginBottom: "16px" }}>
        //     <label
        //       htmlFor={fieldId}
        //       style={{
        //         display: "block",
        //         fontSize: "14px",
        //         fontWeight: 600,
        //         marginBottom: "4px",
        //       }}
        //     >
        //       {field.title || field.name}
        //       {field.required && (
        //         <span style={{ color: "red", marginLeft: 4 }}>*</span>
        //       )}
        //     </label>
        //     <input
        //       id={fieldId}
        //       className="form-input"
        //       type="text"
        //       value={String(value)}
        //       onChange={(e) =>
        //         handleFormFieldChange(field.name, e.target.value || "")
        //       }
        //       disabled={documentStore.isUploading}
        //       placeholder={field.placeholder}
        //       maxLength={field.maxlength}
        //     />
        //     {field.description && (
        //       <div
        //         style={{
        //           fontSize: "12px",
        //           color: "#422509",
        //           marginTop: "4px",
        //         }}
        //       >
        //         {field.description}
        //       </div>
        //     )}
        //   </div>
        // );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [formData, documentStore, handleFormFieldChange],
  );

  // File validation
  const validateFile = useCallback((file: File): string | null => {
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 50MB limit. Current size: ${formatBytes(file.size)}`;
    }

    return null;
  }, []);

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
  }, []);

  // Handle drag leave
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
  }, []);

  // Handle drag enter
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        const file = files[0];
        const error = validateFile(file);

        if (error) {
          setValidationError(error);
          setSelectedFile(null);
        } else {
          setValidationError(null);
          setSelectedFile(file);
        }
      }
    },
    [validateFile],
  );

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        const error = validateFile(file);

        if (error) {
          setValidationError(error);
          setSelectedFile(null);
        } else {
          setValidationError(null);
          setSelectedFile(file);
        }
      }
    },
    [validateFile],
  );

  // Handle file input click
  const handleFileInputClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      setValidationError("Please select a file to upload.");
      return;
    }

    if (!selectedDocumentTypeId) {
      setValidationError("Please select a document type.");
      return;
    }

    if (!documentStore.currentFormSchema) {
      setValidationError("Form schema not loaded. Please wait and try again.");
      return;
    }

    // Validate required fields (exclude Internal fields)
    const requiredFields = documentStore.currentFormSchema.fields.filter(
      (f) => f.type !== "Internal" && f.required,
    );
    const missingFields = requiredFields.filter(
      (f) => !formData[f.name] || formData[f.name] === "",
    );

    if (missingFields.length > 0) {
      // Use custom error message from field.error if available, otherwise use field title
      const errorMessages = missingFields.map(
        (f) => f.error || f.title || f.name,
      );
      if (errorMessages.length === 1) {
        setValidationError(errorMessages[0]);
      } else {
        setValidationError(
          `Please fill in all required fields: ${errorMessages.join(", ")}`,
        );
      }
      return;
    }

    setValidationError(null);

    try {
      // Extract site URL and library from schema
      const siteUrl =
        typeof documentStore.currentFormSchema.site === "string"
          ? documentStore.currentFormSchema.site
          : "";

      const libraryName =
        typeof documentStore.currentFormSchema.library === "string"
          ? documentStore.currentFormSchema.library
          : Array.isArray(documentStore.currentFormSchema.library) &&
              documentStore.currentFormSchema.library.length > 0
            ? documentStore.currentFormSchema.library[0].name
            : "";

      // Build metadata from ALL fields (including Internal fields) - matches legacy Utility.getMetadata
      const rawMetadata = buildDocumentMetadata(
        documentStore.currentFormSchema.fields,
        formData,
      );

      // ⚠️ CONDITIONAL MasterDocumentID: Only add if present in form schema
      // Legacy approach (Utility.getMetadata): MasterDocumentID was only included if defined in FormJSON
      // Some SharePoint libraries (e.g., "Other Contractor Documents", "RBTCertificate", "Level Two Background")
      // don't have MasterDocumentID column, so form schema won't include it
      // buildDocumentMetadata already handles Internal fields from form schema
      // We should NOT unconditionally add MasterDocumentID here - it would cause 400 errors for libraries without the column

      // Replace context values in metadata (matches legacy Utility.replaceContextValues)
      // Stringify, replace placeholders, then parse back
      let metadataString = JSON.stringify(rawMetadata);
      metadataString = replaceContextValues(
        metadataString,
        user,
        selectedFile.name,
      );
      const metadata: DocumentFormData = JSON.parse(metadataString);

      // Get username (without domain) for timestamping
      const userNameOnly = user?.LoginName?.includes("\\")
        ? user.LoginName.split("\\")[1]
        : user?.LoginName || "unknown";

      // Add timestamp and username to filename to prevent conflicts
      const timestampedFileName = addTimeStampToFileName(
        selectedFile.name,
        userNameOnly,
      );

      // Create new File object with timestamped name
      const timestampedFile = new File([selectedFile], timestampedFileName, {
        type: selectedFile.type,
      });

      setIsLoadingSchema(true);
      // Upload document with timestamped filename
      // Document refresh happens automatically in DocumentStore.uploadDocument
      await documentStore.uploadDocument(
        timestampedFile,
        siteUrl,
        libraryName,
        "", // folderPath - can be added to form schema if needed
        metadata,
        true, // overwrite - allow replacing existing files
        user?.LoginName, // user - for proper Author/Editor attribution in SharePoint
        documentStore.selectedState, // userState - for credentialing system integration (new state when the New region toggle is selected)
      );

      // Refresh document list after a delay to allow SharePoint to process the deletion
      if (user?.LoginName) {
        setTimeout(async () => {
          try {
            await documentStore.fetchDocumentsForCurrentUser();
            setIsLoadingSchema(false);
          } catch (refreshError) {}
        }, 2000); // 2-second delay
      }
    } catch (error) {
      console.error("Upload failed", error);
      // Error is already handled by DocumentStore and displayed via UIStore
    }
  }, [selectedFile, selectedDocumentTypeId, formData, documentStore, user]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    uiStore.closeUploadDialog();
  }, [uiStore]);

  if (!uiStore.isUploadDialogOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          width: "90vw",
          maxWidth: 720,
          maxHeight: "90vh",
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          borderRadius: 12,
          boxShadow:
            "0 25px 60px rgba(15,23,42,0.4), 0 10px 20px rgba(15,23,42,0.25)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--app-border-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Upload Document
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            disabled={documentStore.isUploading}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            padding: 16,
            flex: 0,
          }}
        >
          {/* Error Message */}
          {validationError && (
            <div
              style={{
                marginBottom: 16,
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid hsl(var(--destructive))",
                backgroundColor: "hsla(var(--destructive),0.08)",
                fontSize: 13,
              }}
            >
              {validationError}
            </div>
          )}

          {/* Document Type Selection */}
          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor="upload-doc-type"
              style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Document Type
              <span style={{ color: "red", marginLeft: 4 }}>*</span>
            </label>
            {isLoadingTypes && <LoadingSpinner />}
            {!isLoadingTypes && (
              <DocumentDropdown
                id="upload-doc-type"
                placeholder="Select document type"
                options={documentStore.documentTypes
                  .filter((type) => type.EnableUpload !== false) // Hide types not enabled for upload
                  .map((type) => ({
                    key: type.ID.toString(),
                    text: type.Title,
                  }))
                  .sort((a, b) => a.text.localeCompare(b.text))} // Sort alphabetically by title
                value={selectedDocumentTypeId?.toString() ?? ""}
                disabled={documentStore.isUploading}
                onChange={(next) => {
                  const asString = String(next ?? "");
                  const parsed = asString ? parseInt(asString, 10) : NaN;
                  setSelectedDocumentTypeId(
                    Number.isNaN(parsed) ? null : parsed,
                  );
                  setFormData({});
                }}
              />
            )}
          </div>

          {isLoadingSchema && <LoadingSpinner />}

          {/* File Upload Area */}
          {selectedDocumentTypeId && !isLoadingSchema && (
            <>
              {/* Drag-and-Drop Zone */}
              <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleFileInputClick}
                style={{
                  border: `2px dashed ${isDragging ? "#0078d4" : "#8a8886"}`,
                  borderRadius: "4px",
                  padding: "40px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragging ? "#f3f2f1" : "transparent",
                  transition: "all 0.2s ease",
                  marginBottom: "16px",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={documentStore.currentFormSchema?.fileTypes}
                  onChange={handleFileInputChange}
                  style={{
                    display: "none",
                  }}
                  disabled={documentStore.isUploading}
                />
                <Icon
                  icon="line-md:cloud-alt-upload-filled-loop"
                  style={{ fontSize: 52, color: `hsl(var(--primary))` }}
                />
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    marginBottom: "4px",
                  }}
                >
                  {isDragging
                    ? "Drop file here"
                    : "Drag and drop files here or click to browse"}
                </div>
                <div style={{ fontSize: "12px", color: "#605e5c" }}>
                  Maximum file size: 50MB
                </div>
              </div>

              {/* Selected File Info */}
              {selectedFile && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: " hsl(var(--input))",
                    borderRadius: "4px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Icon icon="mdi:file-document" width="24" height="24" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{selectedFile.name}</div>
                    <div style={{ fontSize: "12px", color: "#605e5c" }}>
                      {formatBytes(selectedFile.size)}
                    </div>
                  </div>
                  {!documentStore.isUploading && (
                    <button
                      type="button"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setValidationError(null);
                      }}
                      aria-label="Remove selected file"
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        padding: 4,
                        borderRadius: 9999,
                      }}
                    >
                      <Icon icon="mdi:close" width="20" height="20" />
                    </button>
                  )}
                </div>
              )}

              {/* Upload Progress */}
              {documentStore.isUploading && (
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 9999,
                      backgroundColor: "hsl(var(--muted))",
                      overflow: "hidden",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.max(
                          0,
                          Math.min(100, documentStore.uploadProgress || 0),
                        )}%`,
                        backgroundColor: "hsl(var(--primary))",
                        transition: "width 0.2s ease",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "12px",
                      color: "#605e5c",
                    }}
                  >
                    <span>{documentStore.uploadProgress}%</span>
                    <span>
                      {formatBytes(documentStore.uploadedBytes)} /{" "}
                      {formatBytes(documentStore.totalBytes)}
                    </span>
                  </div>
                </div>
              )}

              {/* Dynamic Form */}
              {documentStore.currentFormSchema?.fields &&
              documentStore.currentFormSchema.fields.length > 0 ? (
                <div style={{ marginBottom: "16px" }}>
                  {documentStore.currentFormSchema.fields
                    .filter((field) => field.type !== "Internal")
                    .map((field) => renderFormField(field))}
                </div>
              ) : selectedDocumentTypeId ? (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#605e5c",
                  }}
                >
                  No additional fields required for this document type.
                </div>
              ) : null}
            </>
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "10px 16px 14px 16px",
            borderTop: "1px solid var(--app-border-color)",
          }}
        >
          <button
            type="button"
            onClick={handleCancel}
            disabled={documentStore.isUploading}
            style={{
              padding: "8px 16px",
              borderRadius: 9999,
              border: "1px solid var(--app-border-color)",
              color: "hsl(var(--foreground))",
              backgroundColor: "hsl(var(--background))",
              cursor: documentStore.isUploading ? "not-allowed" : "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              documentStore.isUploading ||
              !selectedFile ||
              !selectedDocumentTypeId ||
              isLoadingSchema
            }
            style={{
              padding: "8px 16px",
              borderRadius: 9999,
              border: "none",
              backgroundColor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              cursor:
                documentStore.isUploading ||
                !selectedFile ||
                !selectedDocumentTypeId ||
                isLoadingSchema
                  ? "not-allowed"
                  : "pointer",
              fontSize: 14,
              fontWeight: 600,
              boxShadow: "0 10px 25px rgba(15,23,42,0.25)",
              opacity:
                documentStore.isUploading ||
                !selectedFile ||
                !selectedDocumentTypeId ||
                isLoadingSchema
                  ? 0.7
                  : 1,
            }}
          >
            {documentStore.isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
});

export default UploadDocument;
